import React from "react";

// Each era wheel is a minimalist SVG rendered inside a 200x200 viewBox.
// Uses only #FFD400 (yellow) and #111111 (black) — with a bit of #FFFFFF for
// tiny highlights when strictly needed. All variants are radially symmetric so
// they rotate naturally.
const Y = "#FFD400";
const B = "#111111";

const HandWheel = () => (
  <g>
    <circle cx="100" cy="100" r="88" fill="none" stroke={Y} strokeWidth="2" strokeDasharray="4 6" opacity="0.5" />
    <circle cx="100" cy="100" r="60" fill="none" stroke={Y} strokeWidth="1.5" opacity="0.4" />
    <text x="100" y="108" textAnchor="middle" fill={Y} fontFamily="Orbitron" fontWeight="800" fontSize="18" opacity="0.7">START</text>
  </g>
);

const LogWheel = () => (
  <g>
    <circle cx="100" cy="100" r="90" fill={Y} />
    <circle cx="100" cy="100" r="90" fill="none" stroke={B} strokeWidth="4" />
    {[70, 55, 40, 25, 12].map((r) => (
      <circle key={r} cx="100" cy="100" r={r} fill="none" stroke={B} strokeWidth="2" opacity="0.5" />
    ))}
    <path d="M100 12 L100 188" stroke={B} strokeWidth="1.5" opacity="0.35" />
    <path d="M20 100 L180 100" stroke={B} strokeWidth="1.5" opacity="0.35" />
  </g>
);

const WoodWheel = () => (
  <g>
    <circle cx="100" cy="100" r="90" fill={Y} />
    <circle cx="100" cy="100" r="90" fill="none" stroke={B} strokeWidth="6" />
    <circle cx="100" cy="100" r="14" fill={B} />
    {[0, 60, 120].map((a) => (
      <path
        key={a}
        d="M100 15 L100 185"
        stroke={B}
        strokeWidth="6"
        transform={`rotate(${a} 100 100)`}
      />
    ))}
  </g>
);

const SpokedWheel = () => (
  <g>
    <circle cx="100" cy="100" r="90" fill="none" stroke={Y} strokeWidth="10" />
    <circle cx="100" cy="100" r="80" fill="none" stroke={Y} strokeWidth="2" opacity="0.4" />
    {Array.from({ length: 8 }).map((_, i) => (
      <line
        key={i}
        x1="100"
        y1="100"
        x2="100"
        y2="18"
        stroke={Y}
        strokeWidth="5"
        transform={`rotate(${i * 45} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="16" fill={Y} />
    <circle cx="100" cy="100" r="6" fill={B} />
  </g>
);

const WarChariotWheel = () => (
  <g>
    <circle cx="100" cy="100" r="92" fill="none" stroke={Y} strokeWidth="12" />
    <circle cx="100" cy="100" r="78" fill="none" stroke={Y} strokeWidth="3" />
    {Array.from({ length: 6 }).map((_, i) => (
      <g key={i} transform={`rotate(${i * 60} 100 100)`}>
        <line x1="100" y1="100" x2="100" y2="18" stroke={Y} strokeWidth="7" />
        <circle cx="100" cy="30" r="4" fill={Y} />
      </g>
    ))}
    <circle cx="100" cy="100" r="20" fill={Y} />
    <circle cx="100" cy="100" r="10" fill={B} />
    <circle cx="100" cy="100" r="4" fill={Y} />
  </g>
);

const RomanChariotWheel = () => (
  <g>
    <circle cx="100" cy="100" r="92" fill="none" stroke={Y} strokeWidth="8" />
    <circle cx="100" cy="100" r="82" fill="none" stroke={Y} strokeWidth="2" />
    {Array.from({ length: 10 }).map((_, i) => (
      <line
        key={i}
        x1="100"
        y1="100"
        x2="100"
        y2="20"
        stroke={Y}
        strokeWidth="4"
        transform={`rotate(${i * 36} 100 100)`}
      />
    ))}
    {Array.from({ length: 10 }).map((_, i) => (
      <circle
        key={`d${i}`}
        cx="100"
        cy="30"
        r="3"
        fill={Y}
        transform={`rotate(${i * 36} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="18" fill={Y} />
    <circle cx="100" cy="100" r="8" fill={B} />
  </g>
);

const MedievalWagonWheel = () => (
  <g>
    <circle cx="100" cy="100" r="94" fill="none" stroke={Y} strokeWidth="14" />
    <circle cx="100" cy="100" r="80" fill="none" stroke={Y} strokeWidth="4" />
    {Array.from({ length: 12 }).map((_, i) => (
      <line
        key={i}
        x1="100"
        y1="100"
        x2="100"
        y2="18"
        stroke={Y}
        strokeWidth="5"
        transform={`rotate(${i * 30} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="22" fill={Y} />
    <rect x="94" y="94" width="12" height="12" fill={B} />
  </g>
);

const IndustrialWheel = () => (
  <g>
    {/* Gear teeth */}
    {Array.from({ length: 12 }).map((_, i) => (
      <rect
        key={i}
        x="94"
        y="6"
        width="12"
        height="18"
        fill={Y}
        transform={`rotate(${i * 30} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="80" fill={Y} />
    <circle cx="100" cy="100" r="80" fill="none" stroke={B} strokeWidth="4" />
    <circle cx="100" cy="100" r="60" fill={B} />
    <circle cx="100" cy="100" r="60" fill="none" stroke={Y} strokeWidth="3" />
    {Array.from({ length: 6 }).map((_, i) => (
      <line
        key={i}
        x1="100"
        y1="100"
        x2="100"
        y2="40"
        stroke={Y}
        strokeWidth="6"
        transform={`rotate(${i * 60} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="16" fill={Y} />
    <circle cx="100" cy="100" r="6" fill={B} />
  </g>
);

const LocomotiveWheel = () => (
  <g>
    <circle cx="100" cy="100" r="94" fill={Y} />
    <circle cx="100" cy="100" r="94" fill="none" stroke={B} strokeWidth="4" />
    <circle cx="100" cy="100" r="80" fill={B} />
    <circle cx="100" cy="100" r="70" fill="none" stroke={Y} strokeWidth="2" />
    {Array.from({ length: 4 }).map((_, i) => (
      <path
        key={i}
        d="M100 100 L100 30"
        stroke={Y}
        strokeWidth="10"
        strokeLinecap="round"
        transform={`rotate(${i * 90} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="20" fill={Y} />
    <circle cx="100" cy="100" r="8" fill={B} />
    {/* Counterweight */}
    <path d="M100 100 L100 60 A40 40 0 0 1 140 100 Z" fill={Y} opacity="0.9" />
  </g>
);

const BicycleWheel = () => (
  <g>
    <circle cx="100" cy="100" r="94" fill="none" stroke={Y} strokeWidth="4" />
    <circle cx="100" cy="100" r="88" fill="none" stroke={Y} strokeWidth="1.5" opacity="0.5" />
    {Array.from({ length: 24 }).map((_, i) => (
      <line
        key={i}
        x1="100"
        y1="100"
        x2="100"
        y2="12"
        stroke={Y}
        strokeWidth="1.2"
        transform={`rotate(${i * 15} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="10" fill={Y} />
    <circle cx="100" cy="100" r="4" fill={B} />
  </g>
);

const AutomobileWheel = () => (
  <g>
    <circle cx="100" cy="100" r="94" fill={B} />
    <circle cx="100" cy="100" r="94" fill="none" stroke={Y} strokeWidth="3" />
    <circle cx="100" cy="100" r="82" fill="none" stroke={Y} strokeWidth="1.5" opacity="0.5" />
    <circle cx="100" cy="100" r="66" fill={Y} />
    <circle cx="100" cy="100" r="60" fill={B} />
    {Array.from({ length: 5 }).map((_, i) => (
      <path
        key={i}
        d="M100 100 L100 40"
        stroke={Y}
        strokeWidth="10"
        strokeLinecap="round"
        transform={`rotate(${i * 72} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="12" fill={Y} />
  </g>
);

const F1Wheel = () => (
  <g>
    <circle cx="100" cy="100" r="94" fill={B} />
    <circle cx="100" cy="100" r="94" fill="none" stroke={Y} strokeWidth="2" />
    {/* Tread markers */}
    {Array.from({ length: 16 }).map((_, i) => (
      <rect
        key={i}
        x="97"
        y="6"
        width="6"
        height="10"
        fill={Y}
        transform={`rotate(${i * 22.5} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="70" fill={Y} />
    <circle cx="100" cy="100" r="70" fill="none" stroke={B} strokeWidth="3" />
    {Array.from({ length: 5 }).map((_, i) => (
      <path
        key={i}
        d="M100 100 L60 60"
        stroke={B}
        strokeWidth="8"
        strokeLinecap="round"
        transform={`rotate(${i * 72} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="16" fill={B} />
    <circle cx="100" cy="100" r="5" fill={Y} />
  </g>
);

const SpaceWheel = () => (
  <g>
    <circle cx="100" cy="100" r="94" fill="none" stroke={Y} strokeWidth="3" />
    <circle cx="100" cy="100" r="86" fill="none" stroke={Y} strokeWidth="1" opacity="0.5" />
    {/* Mesh wheel: many thin curved spokes */}
    {Array.from({ length: 16 }).map((_, i) => (
      <path
        key={i}
        d="M100 100 Q 130 40 100 14"
        stroke={Y}
        strokeWidth="2"
        fill="none"
        transform={`rotate(${i * 22.5} 100 100)`}
      />
    ))}
    {Array.from({ length: 16 }).map((_, i) => (
      <path
        key={`b${i}`}
        d="M100 100 Q 70 40 100 14"
        stroke={Y}
        strokeWidth="2"
        fill="none"
        opacity="0.7"
        transform={`rotate(${i * 22.5} 100 100)`}
      />
    ))}
    <circle cx="100" cy="100" r="20" fill={Y} />
    <circle cx="100" cy="100" r="10" fill={B} />
    <circle cx="100" cy="100" r="3" fill={Y} />
  </g>
);

// Community wheel — network of connected nodes representing the player community.
const CommunityWheel = () => {
  const nodes = 8;
  return (
    <g>
      {/* Outer rim */}
      <circle cx="100" cy="100" r="92" fill="none" stroke={Y} strokeWidth="3" />
      <circle cx="100" cy="100" r="82" fill="none" stroke={Y} strokeWidth="1" opacity="0.4" />
      {/* Connection lines forming a star pattern */}
      {Array.from({ length: nodes }).map((_, i) => {
        const next = (i + 3) % nodes; // connect to node 3 positions ahead
        const a1 = (i / nodes) * Math.PI * 2 - Math.PI / 2;
        const a2 = (next / nodes) * Math.PI * 2 - Math.PI / 2;
        const r = 62;
        return (
          <line
            key={`c${i}`}
            x1={100 + Math.cos(a1) * r}
            y1={100 + Math.sin(a1) * r}
            x2={100 + Math.cos(a2) * r}
            y2={100 + Math.sin(a2) * r}
            stroke={Y}
            strokeWidth="1.2"
            opacity="0.55"
          />
        );
      })}
      {/* Radial lines from center to each node */}
      {Array.from({ length: nodes }).map((_, i) => (
        <line
          key={`r${i}`}
          x1="100"
          y1="100"
          x2="100"
          y2="38"
          stroke={Y}
          strokeWidth="2"
          opacity="0.8"
          transform={`rotate(${(i * 360) / nodes} 100 100)`}
        />
      ))}
      {/* Community nodes */}
      {Array.from({ length: nodes }).map((_, i) => {
        const a = (i / nodes) * Math.PI * 2 - Math.PI / 2;
        const r = 62;
        return (
          <g key={`n${i}`}>
            <circle
              cx={100 + Math.cos(a) * r}
              cy={100 + Math.sin(a) * r}
              r="8"
              fill={Y}
            />
            <circle
              cx={100 + Math.cos(a) * r}
              cy={100 + Math.sin(a) * r}
              r="3"
              fill={B}
            />
          </g>
        );
      })}
      {/* Central hub */}
      <circle cx="100" cy="100" r="18" fill={Y} />
      <circle cx="100" cy="100" r="10" fill={B} />
      <circle cx="100" cy="100" r="4" fill={Y} />
    </g>
  );
};

const WHEELS = {
  hand: HandWheel,
  log: LogWheel,
  wood_wheel: WoodWheel,
  spoked_wheel: SpokedWheel,
  war_chariot: WarChariotWheel,
  roman_chariot: RomanChariotWheel,
  medieval_wagon: MedievalWagonWheel,
  industrial: IndustrialWheel,
  locomotive: LocomotiveWheel,
  bicycle: BicycleWheel,
  automobile: AutomobileWheel,
  f1: F1Wheel,
  space: SpaceWheel,
  community: CommunityWheel,
};

export const WheelSVG = ({ eraKey = "hand" }) => {
  const Comp = WHEELS[eraKey] || HandWheel;
  return (
    <svg
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
      style={{ filter: "drop-shadow(0 0 24px rgba(255, 212, 0, 0.18))" }}
    >
      <Comp />
    </svg>
  );
};

export default WheelSVG;
